import { Button, Input, Modal } from "@deligoo/ui"
import { FormEvent, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import { toast } from "react-toastify"

import { useAppSelector } from "@/App"
import { selectDriverById, updateDriver } from "@/store/drivers"
import { Driver, Shift } from "@/types"
import { fetchDriverShiftsById, fetchSetShifts } from "@/utils"
import { handleStandardError } from "@/utils/errors"

import cls from "../DriverContextMenu.module.scss"

type DriversPlanShiftsModalProps = {
  driverId: Driver["id"]
  handleClose: () => void
}

const WEEK_DAYS = { monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6, sunday: 0 } as const

const initialShiftsState = Object.entries(WEEK_DAYS).map(([_, dayIndex]) => ({
  shift_start: "",
  shift_end: "",
  day: dayIndex,
  date: [""],
}))

export const DriversPlanShiftsModal = ({ driverId, handleClose }: DriversPlanShiftsModalProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [shifts, setShifts] = useState<Shift[]>(initialShiftsState)

  const { t } = useTranslation()
  const driver = useAppSelector(selectDriverById(driverId))

  const dispatch = useDispatch()

  useEffect(() => {
    async function fetchShifts() {
      const { response, json } = await fetchDriverShiftsById(driverId)
      const shiftsData = json.data?.shifts

      if (response.ok && shiftsData) {
        setShifts(shiftsData)
      }
    }

    fetchShifts()
  }, [driverId])

  if (!driver) return null

  const handleChangeShiftHours = (
    e: React.ChangeEvent<HTMLInputElement>,
    shiftIndex: number,
    type: "shift_start" | "shift_end"
  ) => {
    setShifts((shifts) =>
      shifts.map((shift, index) => {
        if (index === shiftIndex) return { ...shift, [type]: e.target.value }
        return shift
      })
    )
  }

  function handlePopulateTodayShiftHours() {
    const startAt = shifts[0]["shift_start"]
    const endAt = shifts[0]["shift_end"]

    setShifts((shifts) => shifts.map((shift) => ({ ...shift, shift_start: startAt, shift_end: endAt })))
  }

  function handleClearShiftHours(shiftIndex: number) {
    setShifts((shifts) =>
      shifts.map((shift, index) => {
        if (index === shiftIndex) return { ...shift, shift_start: "", shift_end: "" }
        return shift
      })
    )
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()

    if (!driver) return null
    setIsSubmitting(true)

    try {
      const { response, json } = await fetchSetShifts(driverId, shifts)
      const updatedDriver = json?.data

      if (response.ok && updatedDriver) {
        toast.success(t("drivers:plan_work_success"))
        dispatch(updateDriver(updatedDriver))
        handleClose()
      } else {
        handleStandardError(response, json)
      }
    } catch (error) {
      toast.error(t("drivers:plan_work_fail"))
    }
    setIsSubmitting(false)
  }

  return (
    <Modal handleClose={handleClose} className={cls.driversBreakModal} closeBtnPosition="inside">
      <Modal.Title>
        {t("drivers:courier")}: {driver.name}
        <div className="h300 mt-4">{t("drivers:schedule")}</div>
      </Modal.Title>

      <Modal.Content>
        <form onSubmit={handleSubmit}>
          <div>
            {Object.entries(WEEK_DAYS).map(([weekday, index]) => (
              <div className="d-flex align-center justify-between" key={weekday}>
                <span className={cls.day}>
                  {t(weekday)}
                  {weekday === "monday" && (
                    <Button
                      variation="contrast"
                      size="small"
                      className={cls.copy}
                      onClick={handlePopulateTodayShiftHours}
                    >
                      {t("drivers:copy_for_rest_of_week")}
                    </Button>
                  )}
                </span>

                <div className="d-flex align-center">
                  <Input
                    className="mt-2 mb-2"
                    value={shifts[index]["shift_start"] || ""}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handleChangeShiftHours(e, index, "shift_start")
                    }
                    type="time"
                    plain
                    name="shift_start"
                  />
                  <span className="ml-2.5 mr-2.5">-</span>
                  <Input
                    className="mt-2 mb-2"
                    value={shifts[index]["shift_end"] || ""}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChangeShiftHours(e, index, "shift_end")}
                    type="time"
                    plain
                    name="shift_end"
                  />
                  <Button
                    variation="contrast"
                    className="ml-2.5"
                    iconProps={{ name: "close", size: "small" }}
                    onClick={() => handleClearShiftHours(index)}
                  />
                </div>
              </div>
            ))}
          </div>

          <div className={cls.actions}>
            <Button onClick={handleClose} color="muted" variation="outlined">
              {t("cancel")}
            </Button>

            <Button color="primary" type="submit" raised disabled={isSubmitting} autoFocus>
              {t("confirm")}
            </Button>
          </div>
        </form>
      </Modal.Content>
    </Modal>
  )
}
